/**
 * The Atlas's side panels and the rover drive between them, built in the order
 * the composition root in src/atlas.ts used to build them.
 */

import type { AtlasLive, AtlasState } from '../atlas';
import { simApi } from '../sim/api.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';
import type { AtmosphereViewHandle } from './atmosphereView.ts';
import { SOL_LIVE_DEFAULT, type BodySceneHandle } from './bodyScene.ts';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySwitchHandle } from './bodySwitch.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DescentLoadHandle } from './descentLoad.ts';
import { createEventLogClick } from './eventLogClick.ts';
import type { FocusRigHandle } from './focusRig.ts';
import type { HexViewHandle } from './hexView.ts';
import type { InjectsHandle } from './injects.ts';
import type { InputHandle } from './input.ts';
import type { InspectorHandle } from './inspector.ts';
import { createBodyManifest, type BodyManifestHandle } from './manifestPanel.ts';
import type { NavigationHandle } from './navigation.ts';
import type { OverlayViewHandle } from './overlayView.ts';
import type { AtlasGroundGame } from './groundGame.ts';
import type { BuildPanelHandle } from './panels/build.ts';
import type { EventLogHandle } from './panels/eventLog.ts';
import { createLayerPrefs, type LayerPrefsHandle } from './panels/layerPrefs.ts';
import { createPanelLayout, type PanelLayoutHandle } from './panels/layout.ts';
import { createPlotControls, type PlotControlsDeps, type PlotControlsHandle } from './panels/plotControls.ts';
import { createRockTuning, type RockTuningHandle } from './panels/rockTuning.ts';
import { createSiteChips, type SiteChipsHandle } from './panels/siteChips.ts';
import { createTelemetryPanel, type TelemetryPanelHandle } from './panels/telemetry.ts';
import type { PlotBuildHandle } from './plotBuild.ts';
import type { PlotGeometryHandle } from './plotGeometry.ts';
import type { PlotSunHandle } from './plotSun.ts';
import { ARTIFICIAL_FILL_DEFAULT, type PyramidViewHandle } from './pyramidView.ts';
import { createRoverDrive, type RoverDriveHandle } from './roverDrive.ts';
import type { SelectionHandle } from './selection.ts';
import type { SiteGroundHandle } from './siteGround.ts';
import {
  normSiteQuery, selectSiteBody, siteBandOf, siteRegistry, SITE_LIST_BANDS_ABOVE,
  spreadSyntheticSites, syntheticSiteCount, syntheticSites, syntheticSpread,
} from './siteRegistry.ts';
import type { PickHandle } from './pick.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { ViewClockHandle } from './viewClock.ts';
import { createSunriseWarp } from './sunriseWarp.ts';
import { createWarpRow } from './warpRow.ts';

export interface PanelsDeps {
  readonly overlay: HTMLElement;
  own(fn: () => void): void;
  readonly root: HTMLElement;
  readonly devMode: boolean;
  readonly state: AtlasState;
  readonly live: AtlasLive;
  readonly GRID_STAGES: PlotControlsDeps['GRID_STAGES'];
  readonly elSampler: HTMLElement;
  readonly elSamplerHost: HTMLElement;
  readonly pyramidView: PyramidViewHandle;
  readonly bodyScene: BodySceneHandle;
  readonly hexViewSeam: HexViewHandle;
  readonly atmosphereView: AtmosphereViewHandle;
  readonly eventLogHandle: EventLogHandle;
  readonly selection: SelectionHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly plotSun: PlotSunHandle;
  readonly descentLoad: DescentLoadHandle;
  readonly injects: InjectsHandle;
  readonly viewClockHandle: ViewClockHandle;
  readonly bodySwitch: BodySwitchHandle;
  readonly focusRig: FocusRigHandle;
  readonly plotBuild: PlotBuildHandle;
  readonly plotGeometry: PlotGeometryHandle;
  readonly siteGround: SiteGroundHandle;
  readonly ground: AtlasGroundGame;
  /** Bound after this module in atlas.ts, so it hands them over as wraps. */
  zoomIntoBody: NavigationHandle['zoomIntoBody'];
  renderOverlay(): void;
  syncTierToAltitude(): void;
  /** Built after this module, so atlas.ts hands them over through getters. */
  readonly cameraRig: CameraRigHandle;
  readonly overlayView: OverlayViewHandle;
  readonly navigation: NavigationHandle;
  readonly inspector: InspectorHandle;
  readonly input: InputHandle;
  readonly pick: PickHandle;
}

export interface PanelsHandle {
  readonly layerPrefs: LayerPrefsHandle;
  readonly manifest: BodyManifestHandle;
  readonly plotControls: PlotControlsHandle;
  readonly siteChips: SiteChipsHandle;
  readonly roverDrive: RoverDriveHandle;
  readonly buildPanel: BuildPanelHandle;
  readonly telemetryPanel: TelemetryPanelHandle;
  readonly panelLayout: PanelLayoutHandle;
  readonly rockTuningPanel: RockTuningHandle;
}

export function createPanels(deps: PanelsDeps): PanelsHandle {
  const {
    overlay, own, root, devMode, state, live, GRID_STAGES, elSampler, elSamplerHost, pyramidView, bodyScene,
    hexViewSeam, atmosphereView, eventLogHandle, selection, bodyReaders, solarScrub, plotSun, descentLoad, injects,
    viewClockHandle, bodySwitch, focusRig, plotBuild, plotGeometry, siteGround, zoomIntoBody, renderOverlay,
    syncTierToAltitude,
  } = deps;
  const { WARP_COPY_LEVELS } = hexViewSeam;

  const layerPrefs = createLayerPrefs({
    ARTIFICIAL_FILL_DEFAULT, SOL_LIVE_DEFAULT, overlay, own,
    pyramidView, hexViewSeam, bodyScene, atmosphereView, live,
    // Declared further down this closure: the wrap defers the read past
    // construction time instead of tripping its TDZ.
    onLayersOpenChange: (open) => {
      panelLayout.layersOpenChanged(open);
      if (open) siteChips.closeFinder();
    },
  });

  /**
   * The warp levels, at every rung.
   *
   * The sim's own rail carries this row and is hidden while the Atlas owns the
   * screen, so the view draws a second FACE for it - every level, label and
   * pick comes off `simApi.warp`, and a click hands the pick back. The plot
   * controls' sol and year sweeps preview over the clock rather than move it,
   * so on the surface both stand; SUNRISE rides beside the ladder there.
   */
  hexViewSeam.sunrise = createSunriseWarp({ state, solarScrub, plotSun, viewClockHandle, injects });
  hexViewSeam.warpRow = createWarpRow({
    host: overlay.querySelector('#atlas-warp-host') as HTMLElement,
    levels: () => (hexViewSeam.warpCopy ? WARP_COPY_LEVELS : simApi.warp.levels()),
    pick: () => (hexViewSeam.warpCopy ? hexViewSeam.warpCopyPick : simApi.warp.pick()),
    label: () => (hexViewSeam.warpCopy
      ? (WARP_COPY_LEVELS[hexViewSeam.warpCopyPick] ?? WARP_COPY_LEVELS[0]!).label
      : simApi.warp.label()),
    reachable: (idx) => (hexViewSeam.warpCopy ? true : simApi.warp.reachable(idx)),
    reason: (idx) => (hexViewSeam.warpCopy ? null : simApi.warp.reason(idx)),
    notice: () => (hexViewSeam.warpCopy ? null : simApi.warp.notice()),
    setPick: (idx) => {
      if (hexViewSeam.warpCopy) hexViewSeam.warpCopyPick = idx; else simApi.warp.setPick(idx);
    },
    // `atlas-clock-check --leg surface --inject surface-gate`: the orrery rungs only, as it was.
    visible: () => !injects.state.WARP_ROW_TIER_GATE || state.tier <= 2,
    goal: hexViewSeam.sunrise.button,
  });
  own(() => { hexViewSeam.warpRow?.dispose(); hexViewSeam.warpRow = null; hexViewSeam.sunrise = null; });

  /**
   * The body manifest, in the left column above the body card.
   *
   * Up at the orrery rungs only. Below QUADRANGLE that column belongs to the
   * plot controls and this panel would land on them, and the manifest is about
   * choosing a body rather than working one - the way out of a plot is the
   * breadcrumb, which is always up.
   */
  const manifest = createBodyManifest({
    host: overlay,
    rows: () => bodyScene.state.bodyRows,
    focusedBodyId: () => state.bodyId,
    visible: () => state.tier <= 2,
    // The SAME path the double-click aim takes at SYSTEM, so a row's click
    // lands the camera at the ORBIT framing with the whole disc in frame and
    // the card, the breadcrumb, the credit and the honesty overlay follow -
    // including the refusal card a body with no ground raises. The pick path
    // dismisses the last refusal before it raises a new one and this must too,
    // or a row click leaves the previous body's card over this one.
    focus: (id) => {
      deps.inspector.clearFlash();
      zoomIntoBody(id, 'list');
    },
    reading: (row) => deps.inspector.manifestReading(row),
  });
  own(() => manifest.dispose());

  // The sampler card's own clicks (src/atlas/inspector.ts). Wrapped rather
  // than passed direct: inspector is declared further down this closure.
  elSamplerHost.addEventListener('click', (e) => deps.inspector.handleSamplerClick(e));

  createEventLogClick({
    overlay, eventLogHandle, selection, live,
    // Declared further down this closure: the wrap defers the read past
    // construction time instead of tripping its TDZ.
    renderOverlay: () => renderOverlay(),
  });

  const plotControls = createPlotControls({
    overlay, state, GRID_STAGES, bodyReaders, solarScrub, plotSun, descentLoad, selection, pyramidView,
    injects, viewClockHandle, live,
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get cameraRig() { return deps.cameraRig; },
    get overlayView() { return deps.overlayView; },
  });
  const siteChips = createSiteChips({
    SITE_LIST_BANDS_ABOVE, overlay, state, normSiteQuery, selectSiteBody, siteBandOf,
    syntheticSiteCount, syntheticSites, syntheticSpread,
    // A reference here would keep the fixture in a build; the switch test lets it drop.
    spreadSyntheticSites: TEST_SWITCHES ? spreadSyntheticSites : () => [],
    plotControls, bodySwitch, bodyReaders, siteRegistry, live, own,
    closeLayers: () => hexViewSeam.viewLayers?.setOpen(false),
    // The finder's list stands where the Layers list does, so GROUND makes the same room for it.
    onOpenChange: (open) => panelLayout.layersOpenChanged(open),
    // The marker and the globe share one scene frame, so the ship's own point
    // goes through the same body-fixed read a click on the globe does.
    subShipLatLon: () => {
      const m = bodyScene.state.shipMarker;
      if (!m || !m.known || !bodyScene.state.bodyNode) return null;
      return deps.pick.latLonOfHit(m.object.position);
    },
    // Declared further down this closure: the getters defer each read past
    // construction time instead of tripping its TDZ.
    get cameraRig() { return deps.cameraRig; },
    get navigation() { return deps.navigation; },
  });

  const roverDrive = createRoverDrive({
    devMode, focusRig, overlay, solarScrub, plotBuild, eventLogHandle, live,
    // Declared further down this closure: the wraps and getters defer each
    // read past construction time instead of tripping its TDZ.
    syncTierToAltitude: () => syncTierToAltitude(),
    renderOverlay: () => renderOverlay(),
    get buildPanel() { return buildPanel; },
    get telemetryPanel() { return telemetryPanel; },
    get cameraRig() { return deps.cameraRig; },
    get input() { return deps.input; },
  });

  const buildPanel = deps.ground.createBuildPanel({
    overlay, root, siteGround, eventLogHandle, solarScrub, live,
    get overlayView() { return deps.overlayView; },
  });

  const telemetryPanel = createTelemetryPanel({
    focusRig, plotGeometry, roverDrive, solarScrub, live, overlay,
    get cameraRig() { return deps.cameraRig; },
  });

  const panelLayout = createPanelLayout({ overlay, plotControls, elSampler, telemetryPanel, root, own });

  const rockTuningPanel = createRockTuning({ overlay, live });

  return {
    layerPrefs, manifest, plotControls, siteChips, roverDrive, buildPanel, telemetryPanel, panelLayout, rockTuningPanel,
  };
}
